"use client";

import { useEffect } from "react";
import type { HubConnection } from "@microsoft/signalr";
import {
  useSignalRConnection,
  useSignalRConnectionState,
} from "./SignalRContext";
import { SIGNALR_METHODS } from "./events";
import { invokeIfConnected } from "./invokeIfConnected";

const claims = new WeakMap<HubConnection, Map<string, number>>();

function claimsFor(connection: HubConnection) {
  let map = claims.get(connection);
  if (!map) {
    map = new Map();
    claims.set(connection, map);
  }
  return map;
}

export function usePatientChannel(
  practiceId: string,
  patientId: string | null,
): void {
  const connection = useSignalRConnection();
  const isConnected = useSignalRConnectionState();

  useEffect(() => {
    if (!connection || !isConnected || !patientId) return;

    const key = `${practiceId}:${patientId}`;
    const map = claimsFor(connection);
    const count = (map.get(key) ?? 0) + 1;
    map.set(key, count);

    if (count === 1) {
      invokeIfConnected(
        connection,
        SIGNALR_METHODS.JOIN_PATIENT_CHANNEL,
        practiceId,
        patientId,
      );
    }

    return () => {
      const remaining = (map.get(key) ?? 1) - 1;
      if (remaining > 0) {
        map.set(key, remaining);
        return;
      }
      map.delete(key);
      invokeIfConnected(
        connection,
        SIGNALR_METHODS.LEAVE_PATIENT_CHANNEL,
        practiceId,
        patientId,
      );
    };
  }, [connection, isConnected, practiceId, patientId]);
}
